import React, { useCallback, useContext, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Image,
  Text,
  Icon,
  Stack,
  useColorModeValue,
  useBreakpointValue,
  Tooltip,
} from "@chakra-ui/react";
import type { DealFinderTicker } from "../../../../api/tickers/models/DealFinderTicker";
import { useCustomScrollbar } from "hooks/useCustomScrollbar";
import { useButtonProps, useFeatureFlags, useUserPreference, useUserProfile } from "hooks";
import { DealMenuButtons } from "../DealMenuButtons";
import { useCopyValue } from "hooks/useCopies";
import { useMemo } from "react";
import { PurchaseDealLeftSection } from "./PurchaseDealLeftSection";
import { PurchaseDealMiddleSection } from "./PurchaseDealMiddleSection";
import { PurchaseDealRightSection } from "./PurchaseDealRightSection";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import type { RootState } from "state/rootReducer";
import { track } from "api/analytics";
import { DEAL_FINDER_PURCHASE_OPENED, DEAL_FINDER_PURCHASE_CLOSED } from "api/analytics/events";
import { sendMessage } from "state/websocket/operations";
import { v4 as uuid } from "uuid";
import type { RequestEntities } from "types/charliui";
import { getInvestmentRatingColor } from "../utils/dealUtils";
import { SettingsProviderContext } from "screens/panels/settings/SettingsProvider";

interface InvestmentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: DealFinderTicker | null;
  onPurchaseComplete?: (dealId: string) => void;
}

export const PurchaseDealModal: React.FC<InvestmentDetailModalProps> = ({ isOpen, onClose, deal }) => {
  const { fullName, email, id } = useUserProfile();
  const leftColumnRef = useRef<HTMLDivElement>(null);
  const middleColumnRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const leftScrollbarStyle = useCustomScrollbar(leftColumnRef, { width: "1px", barTransparency: 0.1 }).scrollbarStyle;
  const middleScrollbarStyle = useCustomScrollbar(middleColumnRef, { width: "1px", barTransparency: 0.1 }).scrollbarStyle;
  const rightScrollbarStyle = useCustomScrollbar(rightColumnRef, { width: "1px", barTransparency: 0.1 }).scrollbarStyle;
  const ctaButtonProps = useButtonProps("md", "cta");

  const bgColor = useColorModeValue("white", "gray.800");
  const purchaseBgColor = useColorModeValue("#F8F8F8", "gray.900");
  const purchaseSelectedBgColor = useColorModeValue("blue.50", "gray.800");
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const footerBgColor = useColorModeValue("white", "gray.800");
  const headerColor = useColorModeValue("primary.default", "primary.300");
  const purchaseHeading = useCopyValue("copy_investment_purchase_heading");
  const purchaseDescription = useCopyValue("copy_markdown_investment_purchase_description");
  const purchaseDescriptionTier0 = useCopyValue("copy_markdown_investment_purchase_description_tier0");
  const premiumTiers = useCopyValue("copy_deal_finder_premium_tiers");
  const ratingTooltip = useCopyValue("copy_deal_finder_rating_tooltip");
  const { isAnonymous } = useSelector((state: RootState) => state.session, shallowEqual);
  const dispatch = useDispatch();
  const { enable_investment_rating: showInvestmentRating } = useFeatureFlags();

  // Banner/tag colors and promo data
  const comingSoonTagBg = useColorModeValue("teal.200", "blue.800");
  const comingSoonTagFontColor = useColorModeValue("gray.800", "blue.800");
  const rawDealFinderPromo = useCopyValue("copy_deal_finder_promotion_banner");
  const dealFinderPromo = rawDealFinderPromo.length > 0 ? JSON.parse(rawDealFinderPromo[0]) : null;
  const dealFinderPromoBgColor = useColorModeValue(dealFinderPromo?.bgColor || "yellow.500", "gray.300");
  const dealFinderPromoTextColor = useColorModeValue(dealFinderPromo?.textColor || "black", "white");
  const { setActiveStep } = useContext(SettingsProviderContext);
  const hasCompletedOnboardingPhaseOne = useUserPreference("ui_onboarding_completed") as boolean;

  const isDealPremium = useMemo(() => {
    return deal?.pricingTier ? premiumTiers.includes(deal.pricingTier) : false;
  }, [deal?.pricingTier, premiumTiers]);

  // Utility function to strip special characters from deal name
  const sanitizeDealName = useCallback((name: string | undefined): string => {
    if (!name) return "";
    return name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9\s\-_]/g, "")
      .trim();
  }, []);

  // track when modal is opened
  useEffect(() => {
    if (isOpen) {
      const sanitizedDealName = sanitizeDealName(deal?.name);
      track(DEAL_FINDER_PURCHASE_OPENED, {
        dealName: sanitizedDealName,
        dealExchange: deal?.exchange,
        dealTicker: deal?.symbol,
        userName: fullName,
        userEmail: email,
      });
      const entities: RequestEntities = [
        { entity: "company_name", value: sanitizedDealName },
        { entity: "company_stock_exchange", value: deal?.exchange || "" },
        { entity: "company_ticker", value: deal?.symbol || "" },
        { entity: "deal_id", value: deal?.id || "" },
        { entity: "user_name", value: fullName || "" },
        { entity: "user_email", value: email || "" },
      ];
      dispatch(
        sendMessage({
          conversationId: uuid(),
          intent: "/deal_opened",
          entities: entities,
        })
      );
    }
  }, [isOpen, deal, email, fullName, dispatch, sanitizeDealName]);

  const handleOnClose = useCallback(() => {
    const sanitizedDealName = sanitizeDealName(deal?.name);
    track(DEAL_FINDER_PURCHASE_CLOSED, {
      dealName: sanitizedDealName,
      dealExchange: deal?.exchange,
      dealTicker: deal?.symbol,
      userName: fullName,
      userEmail: email,
    });
    onClose();
  }, [deal, email, fullName, onClose, sanitizeDealName]);

  if (!deal) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        className="ch-investment-detail-modal"
        bg={bgColor}
        maxW={{ base: "100%", lg: "1200px" }}
        maxH={{ base: "100%", lg: "90vh" }}
        height={"100%"}
        mt={{ base: 0, lg: "auto" }}
        mb={{ base: 0, lg: "auto" }}
        borderRadius={{ base: 0, lg: "xl" }}>
        <ModalHeader p={2} pl={isMobile ? "1rem" : "2rem"} borderBottom="1px" borderColor={borderColor} position="relative">
          <Stack direction="row" justifyContent="space-between" alignItems="center" height="3rem">
            <Stack direction="row" spacing={"1rem"} alignItems={"baseline"} pt="3px">
              <Box>
                {deal.companyLogoUrl ? (
                  <Image src={deal.companyLogoUrl} alt={`${deal.name} logo`} maxH={isMobile ? "25px" : "30px"} />
                ) : (
                  <Text fontWeight="bold" fontSize="lg" pl="1rem">
                    {deal.name}
                  </Text>
                )}
              </Box>
              <Stack direction="row" spacing={"2px"} alignItems="center">
                <Text color={textColor} fontSize="xs" fontWeight="normal">
                  {deal.exchange}
                </Text>
                <Text color={textColor} fontSize="xs" fontWeight="normal">
                  :
                </Text>
                <Text color={textColor} fontSize="xs" fontWeight="normal">
                  {deal.symbol}
                </Text>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={"1rem"} alignItems="center" mr="2.5rem">
              {isDealPremium && (
                <Stack direction="column" alignItems="center" spacing={0}>
                  <Icon boxSize={6} color="orange.400" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.4" />
                    <path
                      fill="currentColor"
                      d="M12,17.27L18.18,21l-1.64-7.03L22,9.24l-7.19-0.61L12,2L9.19,8.63L2,9.24l5.46,4.73L5.82,21L12,17.27z"
                    />
                  </Icon>
                  <Text fontSize="xs" color="orange.400" fontWeight="medium">
                    Premium
                  </Text>
                </Stack>
              )}

              {deal.investmentRating !== null && showInvestmentRating && (
                <Tooltip label={ratingTooltip} placement="left" maxWidth={"200px"}>
                  <Box
                    zIndex="1"
                    className="ch-dealfinder-tile-rating"
                    fontSize={"16px"}
                    fontWeight={"semibold"}
                    borderRadius={"md"}
                    px="5px"
                    py="5px"
                    bg={getInvestmentRatingColor(deal.investmentRating)}
                    color={textColor}>
                    {deal.investmentRating.toFixed(1)}
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          <ModalCloseButton onClick={() => !isAnonymous && !hasCompletedOnboardingPhaseOne && setActiveStep(0)} />
        </ModalHeader>
        <ModalBody p={0} maxH={{ base: "100%", lg: "95vh" }} overflow={{ base: "auto", lg: "hidden" }}>
          <Box overflow={"hidden"} position="absolute" top="0" left="0" width="9rem" height="9rem">
            {deal.status === "pending" && (
              <Box
                position="absolute"
                top="32px"
                left="-43px"
                zIndex="2"
                width="190px"
                height="40px"
                bg={comingSoonTagBg}
                transform="rotate(-45deg)"
                transformOrigin="center"
                boxShadow="0 6px 8px rgba(0,0,0,0.2)"
                display="flex"
                alignItems="center"
                justifyContent="center">
                <Text fontSize="16px" fontWeight="bold" color={comingSoonTagFontColor} whiteSpace="nowrap">
                  COMING SOON
                </Text>
              </Box>
            )}
            {deal.status !== "pending" && dealFinderPromo && dealFinderPromo.tierFilter && deal.pricingTier === dealFinderPromo.tierFilter && (
              <Box
                position="absolute"
                top="18px"
                left="-36px"
                zIndex="2"
                width="146px"
                height="40px"
                bg={dealFinderPromoBgColor}
                transform="rotate(-45deg)"
                transformOrigin="center"
                boxShadow="0 6px 8px rgba(0,0,0,0.2)"
                display="flex"
                alignItems="center"
                justifyContent="center">
                <Text fontSize="16px" fontWeight="bold" color={dealFinderPromoTextColor} whiteSpace="nowrap">
                  {dealFinderPromo.label}
                </Text>
              </Box>
            )}
          </Box>
          <Stack
            direction={{ base: "column-reverse", lg: "row" }}
            justifyContent={isMobile ? "unset" : "space-evenly"}
            width="100%"
            height={isMobile ? "unset" : "100%"}>
            {/* Left Section */}
            <PurchaseDealLeftSection
              deal={deal}
              leftColumnRef={leftColumnRef}
              leftScrollbarStyle={leftScrollbarStyle}
              borderColor={borderColor}
            />

            {/* Middle Section */}
            <PurchaseDealMiddleSection
              deal={deal}
              middleColumnRef={middleColumnRef}
              middleScrollbarStyle={middleScrollbarStyle}
              textColor={textColor}
              bgColor={bgColor}
              borderColor={borderColor}
            />

            {/* Right Section */}
            <PurchaseDealRightSection
              deal={deal}
              rightColumnRef={rightColumnRef}
              rightScrollbarStyle={rightScrollbarStyle}
              purchaseBgColor={purchaseBgColor}
              purchaseSelectedBgColor={purchaseSelectedBgColor}
              footerBgColor={footerBgColor}
              borderColor={borderColor}
              headerColor={headerColor}
              purchaseHeading={purchaseHeading}
              purchaseDescription={deal.pricingTier === "tier_0" ? purchaseDescriptionTier0 : purchaseDescription}
              id={id}
              ctaButtonProps={ctaButtonProps}
              isMobile={isMobile || false}
              onClose={onClose}
              isAnonymousUser={isAnonymous}
            />
          </Stack>
        </ModalBody>
        <ModalFooter
          py={1}
          px="1rem"
          borderBottomRadius={{ base: 0, lg: "xl" }}
          borderTop="1px"
          bg={footerBgColor}
          borderColor={borderColor}
          justifyContent="space-between"
          width="100%">
          <DealMenuButtons deal={deal} iconSize="1.5rem" showBorder={false} isWideContainer isAnonymousUser={isAnonymous} />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
